import { useTranslation } from 'react-i18next';

import { useTutorAggregateUsage } from '../../../hooks/useTutorAggregateUsage';
import { useTutorUsageFilters } from '../../../hooks/useTutorUsageFilters';
import { UsagePanelStatus } from '../../molecules/UsagePanelStatus';
import { AggregateUsageKpiRow } from '../AggregateUsageKpiRow';
import { MonthlyHistoryList } from '../MonthlyHistoryList';
import { TokenCompositionCard } from '../TokenCompositionCard';
import { TokenTrendChart } from '../TokenTrendChart';

/**
 * Painel da aba "Consumo de tokens": recorte agregado do relatório de tokens no período.
 *
 * Reusa os três cards da tela do aluno (`TokenTrendChart`, `TokenCompositionCard`,
 * `MonthlyHistoryList`) sem alterá-los — recebem dados puros por prop e a copy deles não
 * fala em aluno. É a composição por provedor/operação e o histórico mensal que justificam a
 * aba: o dashboard da aba vizinha não tem nenhum dos dois.
 *
 * É dono da própria consulta: montado só quando a aba está ativa, então a aba inativa não
 * dispara request.
 */
export function TokenUsagePanel() {
  const { t } = useTranslation('tutorAI');
  const { filters } = useTutorUsageFilters();
  const { usage, dailySeries, isLoading, isError, isPeriodValid, refetch } =
    useTutorAggregateUsage();

  return (
    <UsagePanelStatus
      isPeriodValid={isPeriodValid}
      isError={isError}
      errorMessage={t('usage.tokens.loadError')}
      onRetry={() => void refetch()}
    >
      <div className="flex flex-col gap-xs">
        <AggregateUsageKpiRow
          usage={usage}
          periodStart={filters.dateStart}
          periodEnd={filters.dateEnd}
          isLoading={isLoading}
        />
        {/*
         * Os três cards abaixo não recebem `isLoading` — são compartilhados com a tela do
         * aluno e leem coleção vazia como "sem dados", renderizando o próprio estado vazio.
         * Montá-los durante a consulta afirmaria ausência de consumo antes de a resposta
         * existir, então eles só entram depois que `isLoading` termina; o KPI acima já é o
         * indicador de carregamento (seu próprio skeleton).
         */}
        {isLoading ? null : (
          <>
            <TokenTrendChart series={dailySeries} />
            <TokenCompositionCard breakdown={usage.breakdown} />
            <MonthlyHistoryList history={usage.history} />
          </>
        )}
      </div>
    </UsagePanelStatus>
  );
}
